import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { RegionKind, RegionNode, RegionTree } from '@map/shared';
import { archiveRegion, createRegion, renameRegion } from './api.js';

/**
 * The whole planet as an indented list: continents, the countries inside them,
 * the cities inside those. The globe is for reading a world at a glance; this
 * is for rearranging one, which is a job for text.
 */
export interface OutlineProps {
  tree: RegionTree;
  /** Regions this world had no room to draw. */
  unplaced?: Array<{ id: string; reason: string }>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
}

const CHILD_KIND: Record<RegionKind, RegionKind | null> = {
  continent: 'country',
  country: 'city',
  city: 'district',
  district: null,
};

export function Outline({ tree, unplaced = [], selectedId, onSelect, onClose }: OutlineProps) {
  const missing = new Map(unplaced.map((entry) => [entry.id, entry.reason]));
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tree'] });

  const rename = useMutation({
    mutationFn: (input: { id: string; name: string; version: number }) =>
      renameRegion(input.id, input.name, input.version),
    onSuccess: refresh,
  });
  const add = useMutation({
    mutationFn: (input: { parentId: string | null; kind: RegionKind; name: string }) =>
      createRegion({ planetId: tree.planet.id, parentId: input.parentId, kind: input.kind, name: input.name }),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (id: string) => archiveRegion(id, true),
    onSuccess: refresh,
  });

  function rows(ids: string[], depth: number): React.ReactNode {
    return ids.map((id) => {
      const region = tree.regions[id];
      if (!region) return null;
      return (
        <div key={id}>
          <Row
            region={region}
            depth={depth}
            missing={missing.get(id) ?? null}
            selected={id === selectedId}
            onSelect={() => onSelect(id)}
            onRename={(name) => rename.mutate({ id, name, version: region.version })}
            onAdd={(name) => {
              const kind = CHILD_KIND[region.kind];
              if (kind) add.mutate({ parentId: id, kind, name });
            }}
            onArchive={() => archive.mutate(id)}
          />
          {rows(region.children, depth + 1)}
        </div>
      );
    });
  }

  return (
    <aside
      // pt-16 keeps the list clear of the floating nav, which sits over it.
      className="flex h-full w-full flex-col gap-3 overflow-y-auto p-5 pt-16"
      style={{ background: 'var(--surface-0)', borderRight: '1px solid var(--border)' }}
      aria-label="Outline"
    >
      <header className="flex items-center justify-between">
        <h2 className="text-sm">{tree.planet.name}</h2>
        <button onClick={onClose} className="text-text-muted hover:text-text" aria-label="Close the outline">
          ✕
        </button>
      </header>

      <div className="flex flex-col">{rows(tree.roots, 0)}</div>

      {unplaced.length > 0 && (
        <p className="text-xs text-text-muted">
          {unplaced.length === 1 ? 'One place is' : `${unplaced.length} places are`} not drawn:{' '}
          {unplaced[0]?.reason}. A world only has so many landmasses — give this planet a different
          one, or move them inside another continent.
        </p>
      )}

      <AddRow
        label="Add a continent"
        onAdd={(name) => add.mutate({ parentId: null, kind: 'continent', name })}
        indent={0}
      />
    </aside>
  );
}

function Row({
  region,
  depth,
  missing,
  selected,
  onSelect,
  onRename,
  onAdd,
  onArchive,
}: {
  region: RegionNode;
  depth: number;
  /** Why this one is not on the globe, if it is not. */
  missing: string | null;
  selected: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onAdd: (name: string) => void;
  onArchive: () => void;
}) {
  const [name, setName] = useState(region.name);
  const [adding, setAdding] = useState(false);
  const canNest = CHILD_KIND[region.kind] !== null;

  return (
    <div>
      <div
        className="group flex items-center gap-1 rounded-[var(--radius-sm)] py-0.5 pr-1 hover:bg-surface-1"
        style={{ paddingLeft: depth * 14 + 4, background: selected ? 'var(--surface-2)' : undefined }}
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onFocus={onSelect}
          onBlur={() => {
            const next = name.trim();
            if (next && next !== region.name) onRename(next);
            else setName(region.name);
          }}
          className="min-w-0 flex-1 rounded-[var(--radius-sm)] border border-transparent bg-transparent px-1 py-0.5 text-sm focus:border-border"
        />
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-text-muted">
          {missing ? 'not on the map' : region.kind}
        </span>
        {canNest && (
          <button
            onClick={() => setAdding((open) => !open)}
            className="shrink-0 px-1 text-text-muted opacity-0 group-hover:opacity-100 hover:text-text"
            aria-label={`Add inside ${region.name}`}
          >
            +
          </button>
        )}
        <button
          onClick={onArchive}
          className="shrink-0 px-1 text-text-muted opacity-0 group-hover:opacity-100 hover:text-text"
          aria-label={`Archive ${region.name}`}
        >
          ✕
        </button>
      </div>
      {adding && canNest && (
        <AddRow
          label={`New ${CHILD_KIND[region.kind]}`}
          indent={depth + 1}
          onAdd={(value) => {
            onAdd(value);
            setAdding(false);
          }}
        />
      )}
    </div>
  );
}

function AddRow({
  label,
  indent,
  onAdd,
}: {
  label: string;
  indent: number;
  onAdd: (name: string) => void;
}) {
  const [value, setValue] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const name = value.trim();
        if (!name) return;
        onAdd(name);
        setValue('');
      }}
      style={{ paddingLeft: indent * 14 + 4 }}
      className="py-0.5"
    >
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={label}
        className="w-full rounded-[var(--radius-sm)] border border-dashed border-border bg-transparent px-1 py-0.5 text-sm"
      />
    </form>
  );
}
